import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { useDiscountedPrice } from '../../hooks';
import { PriceDisplay } from '../PriceDisplay/PriceDisplay';
import styles from './AmountSummary.module.css';
import { AmountWarning } from './AmountWarning';

interface AmountSummaryProps {
    amountInSubunits: number;
    inputAmount: number;
}

/**
 * Amount and its consequence as one statement (SWRN-1800). With a campaign the
 * two numbers part ways — the machine receives what was typed, the card is
 * charged less — so the caption names the charge and the warning names what
 * reaches the machine.
 */
const AmountSummary = ({
    amountInSubunits,
    inputAmount,
}: AmountSummaryProps): React.JSX.Element => {
    const { t } = useTranslation();
    const vm = useAppStore((store) => store.vm);
    const priceView = useDiscountedPrice({
        amountInSubunits,
        inputAmount,
        discountPercentage: vm?.campaign?.discount_percentage,
    });

    const hasDiscount = priceView.savings > 0;

    return (
        <div className={styles.card}>
            <p className={styles.caption}>
                {hasDiscount ? t('Charged from card') : t('You are sending to the machine')}
            </p>
            <PriceDisplay
                className={styles.priceBox}
                campaignClassName={styles.campaign}
                totalAmount={priceView.discountedAmount}
                originalAmount={priceView.originalAmount}
                savings={priceView.savings}
                currencyCode={vm?.currency_code}
                campaignMessage={vm?.campaign?.message}
                discountPercentage={vm?.campaign?.discount_percentage}
                startHour={vm?.campaign?.start_hour}
                endHour={vm?.campaign?.end_hour}
            />
            <div className={styles.divider} />
            <AmountWarning
                machineAmount={hasDiscount ? priceView.originalAmount : undefined}
                currencyLabel={t<string>(`Currency.${vm?.currency_code}`)}
            />
        </div>
    );
};

export { AmountSummary };
